import { customTextSchema } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { baseSchema } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/baseSchema";
import { prognosisResponseSchema } from "features/CenterContent/shared/operationalInformation/prognosisUtils";
import { stopIdSchema } from "features/CenterContent/shared/operationalInformation/utils";
import { dateTimeSchemaResponse } from "shared/form/dateTimeSchema";
import { z } from "zod";

const openEndedDurationSchema = z.object({
  type: z.literal("OPEN_ENDED"),
  from_time: dateTimeSchemaResponse,
  extension_time_in_minutes: z
    .number()
    .transform((extensionTime) => extensionTime.toString()),
});

export const timedDurationSchema = z.object({
  type: z.literal("TIMED"),
  from_time: dateTimeSchemaResponse,
  to_time: dateTimeSchemaResponse,
});

const periodSchema = z.object({
  id: z.string(),
  from_time: dateTimeSchemaResponse,
  to_time: dateTimeSchemaResponse,
});

export type PeriodResponseDuration = z.infer<typeof periodSchema>;

const periodicDurationSchema = z.object({
  type: z.literal("PERIODIC"),
  periods: z.array(periodSchema),
});

export const durationSchema = z.discriminatedUnion("type", [
  openEndedDurationSchema,
  timedDurationSchema,
  periodicDurationSchema,
]);

export const affectedLegSchema = z.object({
  fromStopId: stopIdSchema,
  toStopId: stopIdSchema,
});
export type LegacyAffectedLegSchema = z.infer<typeof affectedLegSchema>;

const eventStateSchema = z.enum(["OPEN", "CLOSED"]);
const eventPhaseSchema = z.enum([
  "PROPOSAL",
  "DISCARDED",
  "UNPUBLISHED",
  "PUBLISHED",
  "WITHDRAWN",
  "REPLACED",
]);

export const responseSchema = baseSchema.and(
  z.object({
    type: z.enum([
      "INFRASTRUCTURE_GENERAL_INFO",
      "INFRASTRUCTURE_GENERAL_WARNING",
      "INFRASTRUCTURE_PLANNED_MAINTENANCE",
      "INFRASTRUCTURE_STRETCH_CLOSED",
      "INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED",
      "INFRASTRUCTURE_SLOW_DRIVING",
    ]),
    uuid: z.string(),
    referenceId: z.string(),
    stretchName: z.string(),
    countryCode: z.enum(["NO", "SE"]),
    affectedLegs: affectedLegSchema.array(),
    durations: durationSchema,
    message: z.string().nullable(),
    updatedAt: dateTimeSchemaResponse,
    title: z.string().nullable(),
    priority: z.number().int(),
    incidentId: z.string().nullish(),
    prognosis: prognosisResponseSchema,
    actionCard: z.object({ type: z.string().nullable() }).nullable(),
    isirkId: z.string().nullable(),
    redirectStretches: z
      .array(
        z.object({
          name: z.string(),
          legs: z.array(z.object({ fromStop: z.string(), toStop: z.string() })),
        }),
      )
      .max(1),
    distributions: customTextSchema.nullable(),
    state: eventStateSchema,
    phase: eventPhaseSchema.nullable(),
  }),
);

export type InfrastructureResponse = z.infer<typeof responseSchema>;

export const infrastructureEventResponseSchema = z.object({
  infrastructureInformation: responseSchema,
});

export const upcomingEventsResponseSchema = z.array(responseSchema);

export type InfrastructureEventType = z.infer<
  typeof infrastructureEventResponseSchema
>;

export type PeriodicDuration = z.infer<typeof periodicDurationSchema>;

export type Duration = z.infer<typeof durationSchema>;
